import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { allProducts as productsList } from '../data/products';
import API_URL from '../config/api';

const productImageMap = {
  'Cardamom (Green)': '/Product Images/Cardamom Green 090831.png',
  'Cardamom (Black)': '/Product Images/Cardamom Black 090832.png',
  'Cinnamon': '/Product Images/Cinnamon 090611.png',
  'Cloves': '/Product Images/Cloves 090710.png',
  'Black Pepper': '/Product Images/Black Pepper 090411.png',
  'Star Anise': '/Product Images/Star Anise 090922.png',
  'Bay Leaves': '/Product Images/Bay Leaves 091099.png',
  'Nutmeg': '/Product Images/Nutmeg 090811.png',
  'Mace': '/Product Images/Mace 090821.jpeg',
  'Fennel Seeds': '/Product Images/Fennel Seeds 090961.png',
  'Cumin Seeds': '/Product Images/Cumin Seeds 090931.png',
  'Coriander Seeds': '/Product Images/Coriander Seeds 090921.png',
  'Turmeric Root': '/Product Images/Turmeric Root 091030.png',
  'Ginger Powder': '/Product Images/Ginger Powder 091012.png',
  'Garlic Powder': '/Product Images/Garlic Powder 071290.png',
  'Onion Powder': '/Product Images/Onion Powder 071220.png',
  'Turmeric Powder': '/Product Images/Turmeric Powder 091030.png',
  'Red Chili Powder': '/Product Images/Red Chili Powder 090422.png',
  'Cumin Powder': '/Product Images/Cumin Powder 090932.png',
  'Coriander Powder': '/Product Images/Coriander Powder 090922.png',
  'Black Pepper Powder': '/Product Images/Black Pepper Powder 090412.png',
};

const productSpecificationMap = {
  'Cardamom (Green)': ['Color: Green', 'Size: 6mm / 7mm / 8mm+', 'Moisture: < 10%', 'Purity: 98-99%'],
  'Cardamom (Black)': ['Color: Dark Brown', 'Aroma: Smoky, strong', 'Moisture: < 12%', 'Purity: 98%'],
  Cinnamon: ['Type: Ceylon / Cassia', 'Color: Light to dark brown', 'Moisture: < 12%', 'Purity: 98-99%'],
  Cloves: ['Color: Dark brown', 'Moisture: < 10-12%', 'Purity: 98-99%', 'Oil Content: High'],
  'Black Pepper': ['Type: Bold / FAQ', 'Color: Black', 'Density: 500-550 GL', 'Moisture: < 12%', 'Purity: 99%'],
  'Star Anise': ['Shape: Whole star', 'Color: Brown', 'Moisture: < 12%', 'Purity: 98%'],
  'Bay Leaves': ['Color: Green to olive', 'Moisture: < 10%', 'Purity: 98%'],
  Nutmeg: ['Type: Whole', 'Color: Brown', 'Moisture: < 10%', 'Purity: 99%'],
  Mace: ['Color: Yellow to orange-red', 'Form: Blade', 'Moisture: < 10%', 'Purity: 98-99%'],
  'Fennel Seeds': ['Color: Green', 'Moisture: < 10%', 'Purity: 99%', 'Aroma: Sweet'],
  'Cumin Seeds': ['Color: Brown', 'Moisture: < 10%', 'Purity: 99%', 'Processing: Machine Cleaned / Sortex'],
  'Coriander Seeds': ['Color: Light brown', 'Moisture: < 10%', 'Purity: 98-99%'],
  'Turmeric Root': ['Type: Dried fingers / bulbs', 'Color: Yellow', 'Curcumin: 2-5%', 'Moisture: < 10%'],
  'Ginger Powder': ['Color: Light yellow', 'Mesh Size: As required', 'Moisture: < 10%', 'Purity: 99%'],
  'Garlic Powder': ['Color: Off-white', 'Moisture: < 6%', 'Mesh Size: As required', 'Purity: 99%'],
  'Onion Powder': ['Color: Creamy white', 'Moisture: < 6%', 'Mesh Size: As required', 'Purity: 99%'],
  'Turmeric Powder': ['Color: Bright yellow', 'Curcumin: 2-5%', 'Moisture: < 10%', 'Mesh Size: As required'],
  'Red Chili Powder': ['Color: Bright red', 'Pungency: As required (SHU level)', 'Moisture: < 10%', 'Mesh Size: As required'],
  'Cumin Powder': ['Color: Brown', 'Moisture: < 10%', 'Purity: 99%', 'Mesh Size: As required'],
  'Coriander Powder': ['Color: Light brown', 'Moisture: < 10%', 'Purity: 99%'],
  'Black Pepper Powder': ['Color: Grey to black', 'Moisture: < 10%', 'Purity: 99%', 'Mesh Size: As required'],
};

const Products = () => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    // Track page view
    axios.post(`${API_URL}/api/track`, { page: 'products', action: 'page_view' }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setSelectedProduct(null);
      }
    };

    if (selectedProduct) {
      window.addEventListener('keydown', handleEsc);
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [selectedProduct]);

  const allProducts = productsList;

  const getWhatsAppMessage = (productName) => {
    return `Hi, I'm interested in ${productName}. Please share more details and pricing.`;
  };

  const ProductCard = ({ product }) => (
    <div className="bg-[#fffdf8] rounded-2xl border border-[#e7decb] overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <button
        type="button"
        onClick={() => setSelectedProduct(product)}
        className="w-full h-52 sm:h-48 bg-[#f7f1e5] flex items-center justify-center p-2"
      >
        {productImageMap[product.name] ? (
          <img
            src={productImageMap[product.name]}
            alt={product.name}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        ) : (
          <span className="text-gray-400 text-sm">Image Coming Soon</span>
        )}
      </button>
      
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">{product.name}</h3>
        
        <a
          href={`https://wa.me/919902043782?text=${encodeURIComponent(getWhatsAppMessage(product.name))}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#1f3a27] text-white py-2 px-4 rounded-full text-center text-sm font-semibold tracking-wide uppercase hover:bg-[#17301f] transition-colors block"
        >
          Enquiry
        </a>
      </div>
    </div>
  );

  // Filter products by search query
  const filteredProducts = allProducts.filter((product) => {
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.hsn && product.hsn.includes(searchQuery));
    return matchesSearch;
  });

  return (
    <div className="bg-[#f8f3e8] min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-[#1f3a27]" style={{ fontFamily: 'Georgia, serif' }}>Our Products</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Premium quality whole and ground spices for bulk orders and export requirements.
          </p>
        </div>

        {/* Search Results Info */}
        {searchQuery && (
          <div className="mb-6 text-center">
            <p className="text-gray-700">
              Search results for: <span className="font-bold text-primary">"{searchQuery}"</span>
              {filteredProducts.length > 0 ? (
                <span className="ml-2">({filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} found)</span>
              ) : (
                <span className="ml-2 text-red-600">(No products found)</span>
              )}
            </p>
          </div>
        )}

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
            {filteredProducts.map((product, index) => (
              <ProductCard key={index} product={product} />
            ))}
          </div>
        ) : (
          <div className="bg-[#fffdf8] rounded-2xl border border-[#e7decb] shadow-lg p-12 text-center mb-8">
            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your search and try again.
            </p>
            {searchQuery && (
              <button
                onClick={() => window.location.href = '/products'}
                className="bg-[#1f3a27] text-white px-6 py-2 rounded-full text-sm font-semibold tracking-wide uppercase hover:bg-[#17301f] transition-colors"
              >
                View All Products
              </button>
            )}
          </div>
        )}

        {/* Note */}
        <div className="bg-[#f1eadc] border-l-4 border-[#1f3a27] p-6 rounded-2xl">
          <p className="text-gray-700">
            <strong>Note:</strong> For product specifications, availability, and bulk orders,
            please contact us via WhatsApp or email.
          </p>
        </div>

        {selectedProduct && (
          <div
            className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4"
            onClick={() => setSelectedProduct(null)}
          >
            <div
              className="bg-[#fffdf8] rounded-2xl border border-[#e7decb] shadow-xl w-full max-w-4xl overflow-hidden"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900">{selectedProduct.name}</h3>
                <button
                  type="button"
                  onClick={() => setSelectedProduct(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                  aria-label="Close product preview"
                >
                  &times;
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  {productImageMap[selectedProduct.name] ? (
                    <img
                      src={productImageMap[selectedProduct.name]}
                      alt={selectedProduct.name}
                      className="w-full h-72 sm:h-[420px] object-contain bg-white"
                    />
                  ) : (
                    <div className="w-full h-72 sm:h-[420px] flex items-center justify-center text-gray-500">
                      Image Coming Soon
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Specifications</h4>
                  <ul className="space-y-3 text-gray-700 mb-6">
                    <li><strong>Product Name:</strong> {selectedProduct.name}</li>
                    <li><strong>HSN Code:</strong> {selectedProduct.hsn || 'N/A'}</li>
                    <li><strong>Type:</strong> {selectedProduct.category || 'N/A'}</li>
                    <li>
                      <strong>Product Specifications:</strong>
                      <ul className="list-disc ml-5 mt-2 space-y-1">
                        {(productSpecificationMap[selectedProduct.name] || ['Specifications available on request']).map((spec) => (
                          <li key={spec}>{spec}</li>
                        ))}
                      </ul>
                    </li>
                  </ul>

                  <a
                    href={`https://wa.me/919902043782?text=${encodeURIComponent(getWhatsAppMessage(selectedProduct.name))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-[#1f3a27] text-white py-2 px-5 rounded-full text-sm font-semibold tracking-wide uppercase hover:bg-[#17301f] transition-colors"
                  >
                    Enquiry on WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
